import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import {
	CarrierDTOResponse,
	CarriersProjectsList,
	CountMap,
	DIFFERENCE_IN_DAYS,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	RestService,
	SEARCH_TRUCK_TABLE,
	StatsDTO,
	TruckSearchResult,
	TruckSearchTable,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { CapacitySearch } from '@domain/interface/capacity-search';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { CarrierToProjectsMap } from '@domain/interface/carrier-to-projects-map';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { ACTIVE_USERS, ALL_ACCOUNT_USERS } from '@shared/utils/account-users';
import { IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { ProductsService } from '@service/products/products.service';

import { BaseComponent } from '@shared/base/base.component';
import { takeUntil, finalize } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { CapacitySearchFilterService } from '@service/capacitySearchFilter/capacity-search-filter.service';
import { PannelWrapperComponent } from '../capacity-search-panel/capacity-search-pannel/pannel-wrapper/pannel-wrapper.component';

import { PROJECT_CARRIER_MAP, RECOMMENDED_CARRIERS } from '../store/actions/';
import { RecommendedCarriersStore } from '../store/store/recommended-carriers-store';
import { ProjectUtilService } from '@service/project-util/project-util.service';
import { getProjectsFilters } from '../../v2/utils/projects-list';
import { buildRequest } from '../../v2/procurement/procurement-list/build-projects-request';

@Component({
	selector: 'app-recommended-carriers',
	templateUrl: './recommended-carriers.component.html',
	styleUrls: ['./recommended-carriers.component.scss'],
})
export class RecommendedCarriersComponent extends BaseComponent implements OnInit, OnChanges {
	@Input() projectDTO: ProjectDTO = null;
	@Input() usersList: Array<Owner> = null;
	allUsersList: Array<Owner> = null;
	@Input() minLoadsPerWeek: number = null;
	@Input() hideLastColumn: boolean = false; // CN-8312: Hide Lanes/Notes/Projects column for V2/V3 layouts
	@Input() isKanbanView: boolean = false; // CN-8312: Compact card layout for V3 Kanban view
	@Input() showBulkSelectGutter: boolean = false; // CN-8312 V2: render leading checkbox column for bulk select
	@Input() bulkSelectedCarrierIds: Array<string> = []; // CN-8312 V2: ids selected via the bulk-select gutter

	@Output() lanesCount = new EventEmitter<any>();
	@Output() isLoadingCapacitySearch = new EventEmitter<any>();
	@Output() openLaneDetails = new EventEmitter<any>();
	@Output() updateSelectedcarrierIds = new EventEmitter<any>();
	@Output() bulkSelectToggle = new EventEmitter<any>();
	// CN-8312 V2: forwarded from pannel-wrapper. Emits the focused carrier on body
	// click expand and null on collapse so project-details can refresh the Quality
	// / Notes / Tasks panel without touching the gutter checkbox state.
	@Output() panelFocused = new EventEmitter<TruckSearchTable | null>();

	@ViewChild('pannelWrapperComponent') pannelWrapperComponent: PannelWrapperComponent;

	truckSearchResult: Array<TruckSearchResult> = null;
	NUMBER_OF_LANES: number = 0;
	searchFormData: CapacitySearch = null;

	currentProjectsPage = 0;
	projectsPageSize = 10;
	allProjectsList: Array<ProjectDTO> = [];
	projectsNextPage: boolean = false;
	totalNumberOfProjects = null;
	carrierProjectMap: any = {};

	lanesCountStatsList: Array<StatsDTO> = null;
	notesCountMap: CountMap = null;

	isLoadingProjectsList: boolean = false;
	isLoadingLanesList: boolean = false;
	isLoadingCarrierIds: boolean = false;

	truckLaneSearchId;

	carrierIdsOfFilteredProjects: Array<string> = null;
	CARRIERS_LIMIT = null;

	userCarrierMapList: Array<any> = null;
	userCarrierMap: any = {};

	capacitySearchResults: Array<TruckSearchTable> = [];

	matchedCarriers: Array<TruckSearchResult> = null;
	isFilterByTrucksCountRequestInProgress: boolean = false;

	// CN-8312: Track last searched project ID to prevent duplicate searches
	private lastSearchedProjectId: number = null;

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService,
		private cargochief: Cargochief,
		public accountSettings: AccountSettingsService,
		private capacitySearchFilterService: CapacitySearchFilterService,
		private accessCache: AccessCacheService,
		public productsService: ProductsService,
		private store: Store,
		public readonly recommendedCarriersStore: RecommendedCarriersStore,
		private projectUtilService: ProjectUtilService,
		private actions$: Actions
	) {
		super();
		this.CARRIERS_LIMIT = environment.NO_OF_SEARCH_RESULTS_TO_DISPLAY;
		// ngOnChanges can dispatch SEARCH_LANES before ngOnInit runs, so resolve here.
		this.allUsersList = ALL_ACCOUNT_USERS(this.accessCache.getAccountUsers());
	}

	ngOnInit() {
		if (IS_NULL_OR_UNDEFINED(this.usersList)) {
			this.usersList = ACTIVE_USERS(this.accessCache.getAccountUsers());
		}
		// CN-8312: Removed initiateSearch() call from ngOnInit
		// ngOnChanges handles all initialization cases including:
		// - V1/V2: projectDTO available at creation time (ngOnChanges fires before ngOnInit)
		// - V3: projectDTO arrives via async pipe after creation

		// CN-8496: When a carrier is removed from Procured or Engaged, re-run the
		// capacity search so the carrier (now no longer in the project) reappears
		// in Recommended. Without this, the in-place store updates leave the
		// carrier hidden from both lists until a full page reload.
		this.actions$
			.pipe(
				ofType(
					PROJECT_CARRIER_MAP.PROCURED.DELETE_SUCCESS,
					PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.SUCCESS
				),
				takeUntil(this.isDestroyed$)
			)
			.subscribe(() => {
				if (!this.utilService.isNullOrUndefined(this.projectDTO)) {
					this.initiateSearch();
				}
			});
	}

	// CN-8312: Handle projectDTO input for all layout versions
	// This handles both sync (V1/V2) and async (V3) projectDTO binding
	ngOnChanges(changes: SimpleChanges) {
		if (changes.projectDTO) {
			const newProjectDTO = changes.projectDTO.currentValue;

			// Initiate search when projectDTO becomes available and is different from last search
			// This prevents duplicate searches when ngOnChanges fires multiple times
			if (
				!this.utilService.isNullOrUndefined(newProjectDTO) &&
				newProjectDTO.id !== this.lastSearchedProjectId
			) {
				this.lastSearchedProjectId = newProjectDTO.id;
				this.initiateSearch();
			}
		}
	}

	initiateSearch() {
		this.searchFormData = this.utilService.getSearchObjectFromProject(
			this.projectDTO,
			this.CARRIERS_LIMIT
		);
		const { originDeadhead, destinationDeadhead } = this.capacitySearchFilterService.getDeadhead();
		let minLoadsPerWeek = this.capacitySearchFilterService.getMinLoadsPerWeek();
		if (IS_NULL_OR_UNDEFINED(minLoadsPerWeek)) {
			minLoadsPerWeek = this.minLoadsPerWeek;
		}
		this.searchFormData = {
			...this.searchFormData,
			originDeadhead,
			destinationDeadhead,
			minLoadsPerWeek,
		};

		let hasCustomer = IS_NOT_NULL_OR_UNDEFINED(this.projectDTO?.customerDTO?.id);

		// Determine if this is a workflow project (WATERFALL or DEDICATED_LANE)
		const isWorkflowProject = IS_WORKFLOW_PROJECT_TYPE(this.projectDTO?.type);

		// For workflow projects: allow state-only lanes (city can be empty/null if state is present)
		// For other projects: require city-level data for origin and destination
		let hasLane: boolean;
		if (isWorkflowProject) {
			hasLane =
				IS_NOT_EMPTY(this.searchFormData.origin?.state) &&
				IS_NOT_EMPTY(this.searchFormData.destination?.state) &&
				IS_NOT_EMPTY(this.searchFormData.trailerType);
		} else {
			hasLane =
				IS_NOT_EMPTY(this.searchFormData.origin?.city) &&
				IS_NOT_EMPTY(this.searchFormData.origin?.state) &&
				IS_NOT_EMPTY(this.searchFormData.destination?.city) &&
				IS_NOT_EMPTY(this.searchFormData.destination?.state) &&
				IS_NOT_EMPTY(this.searchFormData.trailerType);
		}

		this.store.dispatch(RECOMMENDED_CARRIERS.SET.HAS_LANE({ hasLane }));
		if (hasCustomer || hasLane) {
			this.store.dispatch(
				RECOMMENDED_CARRIERS.SEARCH_LANES({
					searchFormData: this.searchFormData,
					usersList: this.allUsersList,
					projectDTO: this.projectDTO,
					hasLane,
				})
			);
		}
	}

	searchLanes() {
		this.isLoadingLanesList = true;
		const laneSearchUrl = this.urlService.getTruckLaneSearchUrl();
		this.restService
			.POST(laneSearchUrl, this.searchFormData)
			.pipe(
				finalize(() => {
					if (this.truckSearchResult.length > 0) {
						this.getAllProjects();
						this.getAssigneeList();
					}
					this.buildSearchView();
					this.isLoadingLanesList = false;
					this.emitPageLoaded();
				})
			)
			.subscribe(
				(response: any) => {
					if (!this.utilService.isNullOrUndefined(response) && response.data.length > 0) {
						response.data.map((lane) => {
							lane['isLane'] = true;
						});
						if (
							!this.utilService.isNullOrUndefined(this.carrierIdsOfFilteredProjects) &&
							this.carrierIdsOfFilteredProjects.length > 0
						) {
							this.matchedCarriers = response.data.filter(
								(search) =>
									this.carrierIdsOfFilteredProjects.indexOf(search.carrier.carrierHubId) !== -1
							);
							response.data = response.data.filter(
								(search) =>
									this.carrierIdsOfFilteredProjects.indexOf(search.carrier.carrierHubId) == -1
							);
						}
						response.data = response.data.slice(0, this.CARRIERS_LIMIT);
					}
					this.NUMBER_OF_LANES = response.data.length;
					this.truckLaneSearchId =
						response['data'].length > 0 ? response['data'][0]['truckLaneSearchId'] : null;
					this.truckSearchResult = response['data'];
					this.lanesCount.emit(this.NUMBER_OF_LANES);
				},
				(error) => {
					this.utilService.showSnackbar(
						'We could not complete your search. Please try again.',
						SNACKBAR.WARNING,
						SNACKBAR.ACTION
					);
				}
			);
	}

	getAllProjects() {
		this.isLoadingProjectsList = true;
		const filteredFilters = getProjectsFilters(
			[],
			this.accountSettings,
			this.utilService.isUserRoleAdmin(),
			this.cargochief.USER_ID
		);
		let requestDTO = buildRequest(
			this.cargochief.ACCOUNT_ID,
			this.accountSettings.ANNUAL_VOLUME,
			filteredFilters || [],
			this.currentProjectsPage,
			this.projectsPageSize,
			'created',
			'desc',
			[],
			this.cargochief.USER_ID,
			[],
			[]
		);
		requestDTO['withProcuredCarrierIdList'] = true;

		const url = this.urlService.getProjectsList();
		this.restService.POST(url, requestDTO).subscribe(
			(response) => {
				this.allProjectsList = this.allProjectsList.concat(response.data);
				this.projectsNextPage = response.nextPage;
				if (this.projectsNextPage) {
					this.currentProjectsPage += 1;
				}
				this.totalNumberOfProjects = response.totalElements;
				this.isLoadingProjectsList = false;
				this.emitPageLoaded();
				this.buildProjects();
			},
			(error) => {
				this.allProjectsList = [];
				this.isLoadingProjectsList = false;
			}
		);
	}

	buildProjects() {
		if (
			!this.utilService.isNullOrUndefined(this.allProjectsList) &&
			!this.utilService.isNullOrUndefined(this.truckSearchResult) &&
			this.truckSearchResult.length > 0 &&
			this.allProjectsList.length > 0 &&
			!this.isLoadingProjectsList
		) {
			this.carrierProjectMap = {
				...this.projectUtilService.carrierProjectMap(this.truckSearchResult, this.allProjectsList),
			};
		}
	}

	getCarrierIdsOfFilteredProjects() {
		this.isLoadingCarrierIds = true;
		const url = this.urlService.getCarrierIdsByProjectIds();
		this.restService
			.POST(url, { projectIds: [this.projectDTO.id] })
			.pipe(
				finalize(() => {
					this.searchLanes();
				})
			)
			.subscribe(
				(result) => {
					this.carrierIdsOfFilteredProjects = result.data.carrierIDs;
					this.isLoadingCarrierIds = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get all carriers.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isLoadingCarrierIds = false;
				}
			);
	}

	getAssigneeList() {
		let userCarrierMapList: any = this.cargochief.getItem(CARGOCHIEF_KEYS.USER_CARRIER_MAPS);
		if (IS_NOT_EMPTY(userCarrierMapList)) {
			this.userCarrierMapList = JSON.parse(userCarrierMapList);
		} else {
			this.userCarrierMapList = [];
		}
		this.buildUserCarrierMap();
	}

	buildUserCarrierMap() {
		if (
			!this.utilService.isNullOrUndefined(this.userCarrierMapList) &&
			this.userCarrierMapList.length > 0 &&
			!this.utilService.isNullOrUndefined(this.allUsersList) &&
			this.allUsersList.length > 0
		) {
			this.userCarrierMapList.map((cMap: any) => {
				// Names come from the full list: an existing assignee may no longer be ACTIVE.
				const assignee: Owner = this.allUsersList.filter(
					(user) => user.userId == cMap.assigneeId
				)[0];
				if (!this.utilService.isNullOrUndefined(assignee)) {
					this.userCarrierMap[cMap.carrierId] = assignee.name;
				}
			});
		}
	}

	async openFullProfile(data: any) {
		const truckSearchResult = await firstValueFrom(
			this.recommendedCarriersStore.truckSearchResult$
		);
		const capacitySearch = truckSearchResult?.find(
			(search) => search.carrier.carrierHubId === data?.lane?.carrierId
		);
		const payload = {
			carrier: {
				active: capacitySearch?.carrier?.active,
				id: data.lane.carrierId,
				businessName: data.lane.businessName,
				mc: data.lane.firmMc,
				dot: data.lane.firmDot,
				powerUnits: data.lane.powerUnits,
				drivers: data.lane.drivers,
				carrierHQ: data.lane.carrierHQ,
				hazmat: data.lane.hazmat,
				hasTeams: data.lane.hasTeams,
				hasTeamsUpdatedOn: data.lane.hasTeamsUpdatedOn,
				vettingOverallDisplay: capacitySearch?.carrier?.vettingOverallDisplay,
				vettingAuthorityDisplay: capacitySearch?.carrier?.vettingAuthorityDisplay,
				vettingInsuranceDisplay: capacitySearch?.carrier?.vettingInsuranceDisplay,
				vettingOperationsDisplay: capacitySearch?.carrier?.vettingOperationsDisplay,
				vettingOtherDisplay: capacitySearch?.carrier?.vettingOtherDisplay,
				vettingSafetyDisplay: capacitySearch?.carrier?.vettingSafetyDisplay,
				lastVettingTime: capacitySearch?.carrier?.lastVettingTime,
				infractionDetails: capacitySearch?.carrier?.infractionDetails,
				trailers: data.lane.trailers,
				phone: data.lane.phone,
				cellPhone: data.lane.cellPhone,
				businessEmailAddress: data.lane.businessEmailAddress,
				representative1: data.lane.representative1,
			},
		};
		this.openLaneDetails.emit(payload);
	}

	emitPageLoaded() {
		if (!this.isLoadingProjectsList && !this.isLoadingLanesList) {
			this.isLoadingCapacitySearch.emit(false);
		} else {
			this.isLoadingCapacitySearch.emit(true);
		}
	}

	buildSearchView() {
		if (
			!this.utilService.isNullOrUndefined(this.truckSearchResult) &&
			this.truckSearchResult.length > 0
		) {
			this.capacitySearchResults = SEARCH_TRUCK_TABLE(
				this.truckSearchResult,
				!this.utilService.isNullOrUndefined(this.searchFormData)
					? this.searchFormData.destination.state
					: ''
			);
			this.capacitySearchResults.map((truck: TruckSearchTable) => {
				if (
					(!truck.isLane || truck.isTruckAndLane) &&
					!this.utilService.isNullOrUndefined(this.searchFormData.pickupDateStart) &&
					!this.utilService.isNullOrUndefined(this.searchFormData.pickupDateEnd) &&
					DIFFERENCE_IN_DAYS(
						this.searchFormData.pickupDateStart,
						this.searchFormData.pickupDateEnd
					) > 1
				) {
					truck.showAvailableDate = true;
				}
			});
			// using number of top results based on environment
			this.capacitySearchResults = this.capacitySearchResults.splice(
				0,
				environment.NO_OF_SEARCH_RESULTS_TO_DISPLAY
			);
		} else {
			this.capacitySearchResults = [];
		}
	}

	filterByTrucksCount(powerUnits: any) {
		const filteredCapacitySearchResults: Array<TruckSearchTable> = [];
		if (IS_NOT_NULL_OR_UNDEFINED(powerUnits) && this.truckSearchResult?.length > 0) {
			this.isFilterByTrucksCountRequestInProgress = true;
			const carrierIds = this.truckSearchResult?.map((search: TruckSearchResult) => {
				return search.carrier.carrierHubId;
			});
			const url = this.urlService.listCarriers(0, carrierIds.length);
			const payload = {
				carrierIds,
				powerUnits,
			};
			this.restService
				.POST(url, payload)
				.pipe(
					takeUntil(this.isDestroyed$),
					finalize(() => {
						this.isFilterByTrucksCountRequestInProgress = false;
					})
				)
				.subscribe(
					(response) => {
						const carriers: Array<CarrierDTOResponse> = response['data'];
						if (IS_NOT_NULL_OR_UNDEFINED(carriers) && carriers.length > 0) {
							carriers.map((carrier) => {
								const search: TruckSearchTable = this.capacitySearchResults.find(
									(capacitySearchResult) => {
										return capacitySearchResult.carrierId == carrier.id;
									}
								);
								if (IS_NOT_NULL_OR_UNDEFINED(search)) {
									filteredCapacitySearchResults.push(search);
								}
							});
						}
						this.pannelWrapperComponent.updateFilteredList(filteredCapacitySearchResults);
					},
					(error) => {
						this.utilService.showSnackbar(
							'Unable to filter by trucks count.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
					}
				);
		} else {
			this.pannelWrapperComponent.updateFilteredList(this.capacitySearchResults);
		}
	}
}
